/** @jsxImportSource @emotion/react */
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import GradeIcon from '@mui/icons-material/Grade';
import SearchIcon from '@mui/icons-material/Search';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import { IconButton } from '@mui/material';

import { ToastType } from '@components/ui/Toast';
import { useToast } from '@hooks/app/useToast';
import { useAppTheme } from '@hooks/useAppTheme';
import { useUserPermissions } from '@hooks/useUserPermissions';
import { favoriteClientApi } from '@services/user';
import { RootState } from '@state-mgmt/store';
import { setClientId, setFavoriteClient, setTrialId } from '@state-mgmt/slices/appSlice';
import { setCurrentProtocol } from '@state-mgmt/slices/protocol-slice';
import { styles } from './styles';

export interface Props {
  onClose: () => void;
}

const SwitchClientModal = ({ onClose }: Props) => {
  const { t } = useTranslation();

  const theme = useAppTheme({});
  const { isSuperAdmin } = useUserPermissions();
  const { themeMode, clientId, favoriteClientId } = useSelector((state: RootState) => state.app);
  const { clients } = useSelector((state: RootState) => state.client);
  const [searchQuery, setSearchQuery] = useState('');
  const [demoMode, setDemoMode] = useState(false);
  const showToast = useToast();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const showDemoClients = isSuperAdmin && demoMode;

  const visibleClients = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return clients
      .filter(client => !client.archived)
      .filter(client => (showDemoClients ? client.demo : !client.demo))
      .filter(client => !normalizedQuery || client.name.toLowerCase().includes(normalizedQuery))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [clients, searchQuery, showDemoClients]);

  const handleDemoModeToggle = () => {
    setDemoMode(current => !current);
  };

  const handleOnSwitchClientPressed = (id: string) => {
    dispatch(setClientId(id));
    localStorage.setItem('clientId', id);
    dispatch(setTrialId(undefined));
    localStorage.setItem('trialId', '');
    dispatch(setCurrentProtocol(undefined));
    onClose();
    navigate('/splash');
  };

  const handleOnFavoriteClientBtnPressed = async (id: string) => {
    try {
      const value = favoriteClientId === id ? null : id;
      await favoriteClientApi({ favoriteClientId: value });
      dispatch(setFavoriteClient(value));
      if (value) {
        handleOnSwitchClientPressed(id);
      }
    } catch (error) {
      showToast({ type: ToastType.ERROR, children: t('switch-client.error-message') });
    }
  };

  return (
    <div css={styles.container(themeMode)}>
      <Box css={styles.header}>
        <Typography variant="h6" component="h2">
          {t('switch-client.title')}
        </Typography>
        {isSuperAdmin && (
          <Button
            size="small"
            variant={demoMode ? 'contained' : 'outlined'}
            color="warning"
            onClick={handleDemoModeToggle}
            css={styles.demoModeButton}
          >
            {t('switch-client.demo-mode')}
          </Button>
        )}
      </Box>

      <Box css={styles.searchWrap}>
        <TextField
          fullWidth
          placeholder={t('switch-client.search-label')}
          value={searchQuery}
          onChange={event => setSearchQuery(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          css={styles.search}
        />
      </Box>

      {visibleClients.length > 0 ? (
        <List css={styles.list} disablePadding>
          {visibleClients.map(({ id, name }) => (
            <ListItem
              css={[styles.listItem(themeMode), id === clientId && styles.selectedItem(theme, themeMode)]}
              disablePadding
              secondaryAction={
                <IconButton size="small" edge="end" aria-label="favorite" onClick={() => handleOnFavoriteClientBtnPressed(id)}>
                  {favoriteClientId === id ? <GradeIcon fontSize="small" /> : <StarOutlineIcon fontSize="small" />}
                </IconButton>
              }
              key={id}
            >
              <ListItemButton selected={id === clientId} onClick={() => handleOnSwitchClientPressed(id)} css={styles.listItemButton}>
                <ListItemAvatar css={styles.listItemAvatar}>
                  <Avatar css={styles.avatar}>{name.charAt(0)}</Avatar>
                </ListItemAvatar>
                <ListItemText primary={name} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500, noWrap: true }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography color="text.secondary" css={styles.emptyState}>
          {t('switch-client.no-results')}
        </Typography>
      )}
    </div>
  );
};

export default SwitchClientModal;
